import * as React from 'react';
import { Select, type SelectOption } from '@packages/ui/composites/Select';
import { TextInput } from '@packages/ui/primitives/TextInput';
import type {
  FieldCatalogEntry,
  ResolveOptionsContext,
  RuleBuilderOption,
  ValueKind,
} from './types';

export interface RuleValueEditorProps {
  semanticId: string;
  valueKind: ValueKind;
  value: unknown;
  fieldEntry: FieldCatalogEntry;
  readOnly: boolean;
  options: RuleBuilderOption[];
  isLoadingOptions?: boolean;
  onChange: (nextValue: unknown, displayName?: string | null) => void;
}

function toSelectValue(value: unknown, options: RuleBuilderOption[], valueKind: ValueKind): string {
  if (value == null || value === '') return '';
  const str = String(value);
  if (valueKind === 'country') {
    const byAbbr = options.find((o) => o.abbreviation === str);
    if (byAbbr) return byAbbr.value;
  }
  const byValue = options.find((o) => o.value === str);
  return byValue?.value ?? str;
}

export const RuleValueEditor = React.forwardRef<HTMLDivElement, RuleValueEditorProps>(
  function RuleValueEditor(
    {
      semanticId,
      valueKind,
      value,
      fieldEntry,
      readOnly,
      options,
      isLoadingOptions,
      onChange,
    },
    ref,
  ) {
    const selectOptions: SelectOption[] = options.map((o) => ({
      label: o.label,
      value: o.value,
    }));

    if (valueKind === 'text' || valueKind === 'number' || valueKind === 'custom') {
      return (
        <div ref={ref}>
          <TextInput
            semanticId={`${semanticId}Value`}
            value={value == null ? '' : String(value)}
            disabled={readOnly}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    }

    const selectValue = toSelectValue(value, options, valueKind);

    return (
      <div ref={ref}>
        <Select
          semanticId={`${semanticId}Value`}
          options={selectOptions}
          value={
            selectValue
              ? selectOptions.find((o) => o.value === selectValue) ?? null
              : null
          }
          isDisabled={readOnly}
          isLoading={isLoadingOptions}
          placeholder={fieldEntry.label ? `Select ${fieldEntry.label}` : 'Select value'}
          onChange={(opt) => {
            const selected = opt as SelectOption | null;
            if (!selected) {
              onChange('');
              return;
            }
            const full = options.find((o) => o.value === selected.value);
            if (valueKind === 'country' && full?.abbreviation) {
              onChange(full.abbreviation, full.label);
              return;
            }
            onChange(selected.value, selected.label);
          }}
        />
      </div>
    );
  },
);

RuleValueEditor.displayName = 'RuleValueEditor';

export type UseRuleValueOptionsParams = {
  fieldEntry?: FieldCatalogEntry;
  context: ResolveOptionsContext;
  resolveOptions?: (
    entry: FieldCatalogEntry,
    context: ResolveOptionsContext,
  ) => Promise<RuleBuilderOption[]>;
};

export function useRuleValueOptions({
  fieldEntry,
  context,
  resolveOptions,
}: UseRuleValueOptionsParams): { options: RuleBuilderOption[]; isLoading: boolean } {
  const [options, setOptions] = React.useState<RuleBuilderOption[]>(
    fieldEntry?.staticOptions ?? [],
  );
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    if (!fieldEntry) {
      setOptions([]);
      return;
    }
    if (fieldEntry.staticOptions?.length) {
      setOptions(fieldEntry.staticOptions);
      return;
    }
    if (!resolveOptions) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    void resolveOptions(fieldEntry, context).then((rows) => {
      if (!cancelled) {
        setOptions(rows);
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [fieldEntry, context, resolveOptions]);

  return { options, isLoading };
}
